import React, { useState } from 'react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { assetPath } from '../assetPath';

/**
 * Public Layout component for unauthenticated pages (landing, login)
 *
 * Features:
 * - Consistent navbar with PowerKid branding
 * - Language switcher
 * - Responsive design with mobile hamburger menu
 * - Optional login/get started buttons
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.loginPath - Path to login page (default: '/login')
 * @param {boolean} props.showAuthButtons - Whether to show login/get started buttons (default: true)
 * @param {Function} props.t - Translation function
 * @param {string} props.variant - 'default' for dark navbar, 'transparent' for transparent navbar over hero
 */
export const PublicLayout = ({
  children,
  loginPath = '/login',
  showAuthButtons = true,
  t = (key) => key,
  variant = 'default'
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isTransparent = variant === 'transparent';
  const navbarClasses = isTransparent
    ? 'navbar navbar-expand-lg navbar-dark position-absolute w-100'
    : 'navbar navbar-expand-lg navbar-dark bg-dark';
  const navbarStyle = isTransparent
    ? { top: 0, left: 0, zIndex: 1000, background: 'transparent' }
    : {};

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Top Navbar */}
      <nav className={navbarClasses} style={navbarStyle}>
        <div className="container">
          {/* Logo */}
          <a href="/" className="navbar-brand d-flex align-items-center" data-testid="login-logo">
            <span data-testid="login-logo-image">
              <img src={assetPath('powerkid-icon.png')} alt="PowerKid Logo" width="30" height="30" className="me-2" />
            </span>
            <span className="fw-bold">PowerKid</span>
          </a>

          {/* Mobile: Hamburger button */}
          <button
            className="navbar-toggler border-0 d-lg-none"
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation"
            aria-expanded={mobileMenuOpen}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Desktop: Nav items */}
          <div className="collapse navbar-collapse justify-content-end d-none d-lg-flex">
            <ul className="navbar-nav align-items-center">
              {showAuthButtons && (
                <>
                  <li className="nav-item">
                    <a href={loginPath} className="nav-link">
                      {t('layouts.navigation.sign_in')}
                    </a>
                  </li>
                  <li className="nav-item">
                    <a href={loginPath} className="btn btn-primary ms-2">
                      {t('layouts.navigation.get_started')}
                    </a>
                  </li>
                </>
              )}
              <li className="nav-item ms-2">
                <LanguageSwitcher />
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div
          className="d-lg-none bg-dark"
          style={isTransparent ? { position: 'absolute', top: '56px', left: 0, right: 0, zIndex: 999 } : {}}
        >
          <div className="container py-3">
            <ul className="navbar-nav">
              {showAuthButtons && (
                <>
                  <li className="nav-item">
                    <a
                      href={loginPath}
                      className="nav-link text-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('layouts.navigation.sign_in')}
                    </a>
                  </li>
                  <li className="nav-item mt-2">
                    <a
                      href={loginPath}
                      className="btn btn-primary w-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('layouts.navigation.get_started')}
                    </a>
                  </li>
                </>
              )}
              <li className="nav-item mt-3 pt-3 border-top border-secondary">
                <LanguageSwitcher isMobile={true} />
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-fill">
        {children}
      </main>
    </div>
  );
};

export default PublicLayout;
